import { apiClient, rawHttpClient } from "./client";
import { isAbortError, toApiError } from "./errors";

export type RunStatus = "queued" | "processing" | "completed" | "failed";
export type LlmProvider = "ollama" | "openai";
export type PageType = "fattura" | "ddt" | "polizza_carico" | "unknown";
export type PageErrorType =
  | "upstream_timeout"
  | "upstream_connection"
  | "unexpected";

export interface MismatchedPair {
  bl: string;
  container: string;
}

export interface MissingPairPinpoint {
  bl: string;
  expected_container: string;
  candidate_ddt_page_index?: number | null;
  candidate_bl?: string | null;
  candidate_container?: string | null;
  similarity?: number | null;
  match_type?: "exact_bl" | "fuzzy_bl" | "container_fallback" | null;
}

export interface FatturaMismatch {
  fattura_page_index: number;
  missing_pairs?: MismatchedPair[];
  matched_pairs?: MismatchedPair[];
  missing_pair_pinpoints?: MissingPairPinpoint[];
}

export interface PageFailure {
  page_index: number;
  image_path: string;
  error_type: PageErrorType;
  detail: string;
}

export interface PageQuality {
  score: number;
  level: "high" | "medium" | "low";
  reasons?: string[];
  metrics?: Record<string, unknown>;
}

export interface PageResult {
  page_index: number;
  page_type: PageType;
  invoice_number?: string | null;
  invoice_date?: string | null;
  invoice_page?: number | null;
  code_groups?: string[];
  classification_details?: Record<string, unknown>;
  raw_text?: string | null;
  quality?: PageQuality | null;
  error_type?: PageErrorType | null;
  error_detail?: string | null;
}

export interface AnalysisResponse {
  run_id: number | null;
  pages: PageResult[];
  correct_fatture: number[];
  mismatches: FatturaMismatch[];
  page_failures: PageFailure[];
  partial_failure: boolean;
  input_filename: string | null;
  total_fattura_containers: number;
  pdf_base64: string | null;
}

export interface ExtractBase64Request {
  filename: string;
  file_b64: string;
  pdf_render_dpi?: string | null;
}

export interface ExtractAcceptedResponse {
  run_id: number;
  status: RunStatus;
  status_url: string;
  result_url: string;
}

export interface RunStatusResponse {
  run_id: number;
  status: RunStatus;
  input_filename?: string | null;
  render_dpi_used?: number | null;
  total_pages?: number;
  processed_pages?: number;
  failed_pages?: number;
  fatture_pages?: number;
  ddt_pages?: number;
  unknown_pages?: number;
  correct_fatture_count?: number;
  mismatches_count?: number;
  reconciliation_status?: string | null;
  current_page?: number | null;
  progress_pct?: number;
  started_at?: string | null;
  finished_at?: string | null;
  last_error?: string | null;
}

export interface RunStatusListResponse {
  total?: number;
  limit?: number;
  offset?: number;
  items?: RunStatusResponse[];
}

export interface PolizzaBLContainers {
  bl: string;
  containers?: string[];
  container_count?: number;
}

export interface PolizzaExtractResponse {
  polizza_id: number | null;
  input_filename: string | null;
  pages: PageResult[];
  bl_groups: PolizzaBLContainers[];
  total_bls: number;
  total_containers: number;
  page_failures: PageFailure[];
  partial_failure: boolean;
}

export interface PolizzaExtractAcceptedResponse {
  polizza_id: number;
  status: RunStatus;
  status_url: string;
  result_url: string;
}

export interface PolizzaRunStatusResponse {
  polizza_id: number;
  status: RunStatus;
  input_filename?: string | null;
  render_dpi_used?: number | null;
  total_pages?: number;
  processed_pages?: number;
  failed_pages?: number;
  current_page?: number | null;
  progress_pct?: number;
  started_at?: string | null;
  finished_at?: string | null;
  last_error?: string | null;
}

export interface PolizzaRunStatusListResponse {
  total?: number;
  limit?: number;
  offset?: number;
  items?: PolizzaRunStatusResponse[];
}

export interface ReconcileBLResult {
  bl: string;
  status: "aligned" | "under" | "over" | "mixed";
  expected_count?: number;
  found_count?: number;
  expected_containers?: string[];
  found_containers?: string[];
  missing_containers?: string[];
  extra_containers?: string[];
  ddt_mismatch_count?: number;
  ddt_mismatch_containers?: string[];
  fattura_mismatch_count?: number;
  fattura_mismatch_containers?: string[];
  ddt_mismatch_details?: ReconcileMismatchDetail[];
  fattura_mismatch_details?: ReconcileMismatchDetail[];
  duplicate_pairs_count?: number;
  duplicate_pairs?: ReconcileDuplicatePair[];
  duplicate_pairs_details?: ReconcileDuplicatePairDetail[];
}

export interface ReconcileDuplicatePair {
  bl: string;
  container: string;
  fatture_count?: number;
  source_documents_count?: number;
  invoice_numbers?: string[];
  source_document_sha256s?: string[];
}

export interface ReconcileResponse {
  generated_at: string;
  bl_filter?: string | null;
  source_document_sha256?: string | null;
  only_open?: boolean;
  strict_expected_only?: boolean;
  total_bls?: number;
  aligned_bls?: number;
  open_bls?: number;
  items?: ReconcileBLResult[];
}

export interface ReconcileMismatchDetail {
  container: string;
  invoice_numbers?: string[];
}

export interface ReconcileDuplicateFatturaRef {
  fattura_id: number;
  invoice_number?: string | null;
  invoice_date?: string | null;
  invoice_page?: number | null;
  source_document_sha256: string;
  input_filename?: string | null;
  last_analysis_run_id?: number | null;
}

export interface ReconcileDuplicatePairDetail {
  bl: string;
  container: string;
  fatture?: ReconcileDuplicateFatturaRef[];
}

export interface BLRelatedDocumentItem {
  source_document_id: number;
  source_document_sha256: string;
  input_filename?: string | null;
  file_size?: number | null;
  first_seen_at: string;
  last_seen_at: string;
  seen_count?: number;
  has_polizza?: boolean;
  has_analysis?: boolean;
  polizza_pairs_count?: number;
  analysis_pairs_count?: number;
  last_polizza_run_id?: number | null;
  last_analysis_run_id?: number | null;
}

export interface BLRelatedDocumentsResponse {
  bl: string;
  total_documents?: number;
  items?: BLRelatedDocumentItem[];
}

export interface SourceDocumentFileBase64Response {
  source_document_sha256: string;
  input_filename?: string | null;
  media_type?: string;
  pdf_base64: string;
}

export interface SourceDocumentReportBase64Response {
  source_document_sha256: string;
  input_filename?: string | null;
  analysis_run_id: number;
  media_type?: string;
  pdf_base64: string;
}

export interface IngestedSourceDocumentItem {
  source_document_id: number;
  source_document_sha256: string;
  input_filename?: string | null;
  file_size?: number | null;
  first_seen_at: string;
  last_seen_at: string;
  seen_count?: number;
  fatture_count?: number;
  polizza_pairs_count?: number;
}

export interface IngestedSourceDocumentListResponse {
  total?: number;
  limit?: number;
  offset?: number;
  items?: IngestedSourceDocumentItem[];
}

export interface IngestedFatturaItem {
  fattura_id: number;
  source_document_id: number;
  source_document_sha256: string;
  input_filename?: string | null;
  page_index: number;
  invoice_number?: string | null;
  invoice_date?: string | null;
  invoice_page?: number | null;
  first_seen_at: string;
  last_seen_at: string;
  seen_count?: number;
  last_analysis_run_id?: number | null;
}

export interface IngestedFatturaListResponse {
  total?: number;
  limit?: number;
  offset?: number;
  items?: IngestedFatturaItem[];
}

export interface IngestedAnalysisPairItem {
  pair_id: number;
  fattura_id: number;
  source_document_id: number;
  source_document_sha256: string;
  input_filename?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  invoice_page?: number | null;
  bl: string;
  container: string;
  status: string;
  first_seen_at: string;
  last_seen_at: string;
  seen_count?: number;
  last_analysis_run_id?: number | null;
}

export interface IngestedAnalysisPairListResponse {
  total?: number;
  limit?: number;
  offset?: number;
  items?: IngestedAnalysisPairItem[];
}

export interface IngestedPolizzaPairItem {
  pair_id: number;
  source_document_id: number;
  source_document_sha256: string;
  input_filename?: string | null;
  bl: string;
  container?: string | null;
  first_seen_at: string;
  last_seen_at: string;
  seen_count?: number;
  last_polizza_run_id?: number | null;
}

export interface IngestedPolizzaPairListResponse {
  total?: number;
  limit?: number;
  offset?: number;
  items?: IngestedPolizzaPairItem[];
}

export interface CommonExtractOptions {
  continueOnPageError?: boolean;
  async?: boolean;
  pdfRenderDpi?: number | null;
  codesLlmProvider?: LlmProvider | null;
}

type SubmitMultipartOptions = CommonExtractOptions & {
  file: File;
};

type SubmitBase64Options = CommonExtractOptions & {
  payload: ExtractBase64Request;
};

interface ListPaginationOptions {
  limit?: number;
  offset?: number;
}

interface ListRunsOptions extends ListPaginationOptions {
  status?: string | null;
  inputFilenameContains?: string | null;
  signal?: AbortSignal;
}

interface ReconcileOptions {
  bl?: string | null;
  sourceDocumentSha256?: string | null;
  onlyOpen?: boolean;
  strictExpectedOnly?: boolean;
  signal?: AbortSignal;
}

interface ListIngestedSourceDocumentsOptions extends ListPaginationOptions {
  sourceDocumentSha256?: string | null;
  inputFilenameContains?: string | null;
}

interface ListIngestedFattureOptions extends ListPaginationOptions {
  sourceDocumentSha256?: string | null;
  invoiceNumber?: string | null;
}

interface ListIngestedAnalysisPairsOptions extends ListPaginationOptions {
  sourceDocumentSha256?: string | null;
  bl?: string | null;
  status?: string | null;
}

interface ListIngestedPolizzaPairsOptions extends ListPaginationOptions {
  sourceDocumentSha256?: string | null;
  bl?: string | null;
}

function toQuery(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    query.set(key, String(value));
  });

  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
}

function buildExtractQuery(options: CommonExtractOptions): string {
  const provider = options.codesLlmProvider ?? null;

  return toQuery({
    async: options.async ?? false,
    continue_on_page_error: options.continueOnPageError ?? true,
    pdf_render_dpi: options.pdfRenderDpi,
    codes_llm_provider: provider,
  });
}

function buildExtractFormData(options: {
  file?: File;
  payload?: ExtractBase64Request;
}): FormData {
  const formData = new FormData();

  if (options.file) {
    formData.append("pdf", options.file);
  }

  if (options.payload) {
    formData.append("payload", JSON.stringify(options.payload));
  }

  if (!options.file && !options.payload) {
    throw new Error("Either file or payload must be provided.");
  }

  return formData;
}

export async function submitExtractMultipart(
  options: SubmitMultipartOptions,
): Promise<AnalysisResponse | ExtractAcceptedResponse> {
  try {
    return await apiClient
      .post(`extract${buildExtractQuery(options)}`, {
        body: buildExtractFormData({ file: options.file }),
      })
      .json<AnalysisResponse | ExtractAcceptedResponse>();
  } catch (error) {
    throw await toApiError(error);
  }
}

export async function submitExtractBase64(
  options: SubmitBase64Options,
): Promise<AnalysisResponse | ExtractAcceptedResponse> {
  try {
    return await apiClient
      .post(`extract${buildExtractQuery(options)}`, {
        body: buildExtractFormData({ payload: options.payload }),
      })
      .json<AnalysisResponse | ExtractAcceptedResponse>();
  } catch (error) {
    throw await toApiError(error);
  }
}

export async function submitExtractPolizzaMultipart(
  options: SubmitMultipartOptions,
): Promise<PolizzaExtractResponse | PolizzaExtractAcceptedResponse> {
  try {
    return await apiClient
      .post(`extract/polizza${buildExtractQuery(options)}`, {
        body: buildExtractFormData({ file: options.file }),
      })
      .json<PolizzaExtractResponse | PolizzaExtractAcceptedResponse>();
  } catch (error) {
    throw await toApiError(error);
  }
}

export async function submitExtractPolizzaBase64(
  options: SubmitBase64Options,
): Promise<PolizzaExtractResponse | PolizzaExtractAcceptedResponse> {
  try {
    return await apiClient
      .post(`extract/polizza${buildExtractQuery(options)}`, {
        body: buildExtractFormData({ payload: options.payload }),
      })
      .json<PolizzaExtractResponse | PolizzaExtractAcceptedResponse>();
  } catch (error) {
    throw await toApiError(error);
  }
}

export async function listRuns(
  options: ListRunsOptions = {},
): Promise<RunStatusListResponse> {
  try {
    return await apiClient
      .get(
        `runs${toQuery({
          status: options.status,
          input_filename_contains: options.inputFilenameContains,
          limit: options.limit,
          offset: options.offset,
        })}`,
        { signal: options.signal },
      )
      .json<RunStatusListResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getRunStatus(
  runId: number,
  options: { signal?: AbortSignal } = {},
): Promise<RunStatusResponse> {
  try {
    return await apiClient
      .get(`runs/${runId}/status`, { signal: options.signal })
      .json<RunStatusResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getRunStatusByUrl(
  url: string,
  options: { signal?: AbortSignal } = {},
): Promise<RunStatusResponse> {
  try {
    return await rawHttpClient
      .get(url, { signal: options.signal })
      .json<RunStatusResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getRunResult(
  runId: number,
  options: { signal?: AbortSignal } = {},
): Promise<AnalysisResponse> {
  try {
    return await apiClient
      .get(`runs/${runId}/result`, { signal: options.signal })
      .json<AnalysisResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getRunResultByUrl(
  url: string,
  options: { signal?: AbortSignal } = {},
): Promise<AnalysisResponse> {
  try {
    return await rawHttpClient
      .get(url, { signal: options.signal })
      .json<AnalysisResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function deleteRun(runId: number): Promise<void> {
  try {
    await apiClient.delete(`runs/${runId}`);
  } catch (error) {
    throw await toApiError(error);
  }
}

export async function listPolizzaRuns(
  options: ListRunsOptions = {},
): Promise<PolizzaRunStatusListResponse> {
  try {
    return await apiClient
      .get(
        `polizze${toQuery({
          status: options.status,
          input_filename_contains: options.inputFilenameContains,
          limit: options.limit,
          offset: options.offset,
        })}`,
        { signal: options.signal },
      )
      .json<PolizzaRunStatusListResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getPolizzaStatus(
  polizzaId: number,
  options: { signal?: AbortSignal } = {},
): Promise<PolizzaRunStatusResponse> {
  try {
    return await apiClient
      .get(`polizze/${polizzaId}/status`, { signal: options.signal })
      .json<PolizzaRunStatusResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getPolizzaResult(
  polizzaId: number,
  options: { signal?: AbortSignal } = {},
): Promise<PolizzaExtractResponse> {
  try {
    return await apiClient
      .get(`polizze/${polizzaId}/result`, { signal: options.signal })
      .json<PolizzaExtractResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function deletePolizzaRun(polizzaId: number): Promise<void> {
  try {
    await apiClient.delete(`polizze/${polizzaId}`);
  } catch (error) {
    throw await toApiError(error);
  }
}

export interface ProcessingStatusResponse {
  has_active_runs: boolean;
  analysis_active: boolean;
  polizza_active: boolean;
  analysis_queued: number;
  analysis_processing: number;
  polizza_queued: number;
  polizza_processing: number;
  total_active_runs: number;
}

export async function getProcessingStatus(
  options: { signal?: AbortSignal } = {},
): Promise<ProcessingStatusResponse> {
  try {
    return await apiClient
      .get("processing-status", { signal: options.signal })
      .json<ProcessingStatusResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function reconcile(
  options: ReconcileOptions = {},
): Promise<ReconcileResponse> {
  try {
    return await apiClient
      .get(
        `reconcile${toQuery({
          bl: options.bl,
          source_document_sha256: options.sourceDocumentSha256,
          only_open: options.onlyOpen ?? true,
          strict_expected_only: options.strictExpectedOnly ?? true,
        })}`,
        { signal: options.signal },
      )
      .json<ReconcileResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getBlRelatedDocuments(
  bl: string,
  options: { signal?: AbortSignal } = {},
): Promise<BLRelatedDocumentsResponse> {
  try {
    return await apiClient
      .get(`bl/${encodeURIComponent(bl)}/documents`, { signal: options.signal })
      .json<BLRelatedDocumentsResponse>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getSourceDocumentFile(
  sourceDocumentSha256: string,
  options: { signal?: AbortSignal } = {},
): Promise<SourceDocumentFileBase64Response> {
  try {
    return await apiClient
      .get(`ingested/source-documents/${sourceDocumentSha256}/file`, {
        signal: options.signal,
      })
      .json<SourceDocumentFileBase64Response>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function getSourceDocumentReport(
  sourceDocumentSha256: string,
  options: { signal?: AbortSignal } = {},
): Promise<SourceDocumentReportBase64Response> {
  try {
    return await apiClient
      .get(`ingested/source-documents/${sourceDocumentSha256}/report`, {
        signal: options.signal,
      })
      .json<SourceDocumentReportBase64Response>();
  } catch (error) {
    if (isAbortError(error)) {
      throw error;
    }
    throw await toApiError(error);
  }
}

export async function listIngestedSourceDocuments(
  options: ListIngestedSourceDocumentsOptions = {},
): Promise<IngestedSourceDocumentListResponse> {
  try {
    return await apiClient
      .get(
        `ingested/source-documents${toQuery({
          source_document_sha256: options.sourceDocumentSha256,
          input_filename_contains: options.inputFilenameContains,
          limit: options.limit,
          offset: options.offset,
        })}`,
      )
      .json<IngestedSourceDocumentListResponse>();
  } catch (error) {
    throw await toApiError(error);
  }
}

export async function listIngestedFatture(
  options: ListIngestedFattureOptions = {},
): Promise<IngestedFatturaListResponse> {
  try {
    return await apiClient
      .get(
        `ingested/fatture${toQuery({
          source_document_sha256: options.sourceDocumentSha256,
          invoice_number: options.invoiceNumber,
          limit: options.limit,
          offset: options.offset,
        })}`,
      )
      .json<IngestedFatturaListResponse>();
  } catch (error) {
    throw await toApiError(error);
  }
}

export async function listIngestedAnalysisPairs(
  options: ListIngestedAnalysisPairsOptions = {},
): Promise<IngestedAnalysisPairListResponse> {
  try {
    return await apiClient
      .get(
        `ingested/analysis-pairs${toQuery({
          source_document_sha256: options.sourceDocumentSha256,
          bl: options.bl,
          status: options.status,
          limit: options.limit,
          offset: options.offset,
        })}`,
      )
      .json<IngestedAnalysisPairListResponse>();
  } catch (error) {
    throw await toApiError(error);
  }
}

export async function listIngestedPolizzaPairs(
  options: ListIngestedPolizzaPairsOptions = {},
): Promise<IngestedPolizzaPairListResponse> {
  try {
    return await apiClient
      .get(
        `ingested/polizza-pairs${toQuery({
          source_document_sha256: options.sourceDocumentSha256,
          bl: options.bl,
          limit: options.limit,
          offset: options.offset,
        })}`,
      )
      .json<IngestedPolizzaPairListResponse>();
  } catch (error) {
    throw await toApiError(error);
  }
}
